import { teamAPI } from '../api'
import { TeamInfoResponse, UpdateTeamRequest } from '../api/openapi'
import {
  EmployeeInfoResponse,
  TeamDeepResponse,
  TeamResponse,
  TeamStatusDTO,
  TeamTreeResponse,
  EmployeeOverviewProjection,
} from '../api/openapi'
import { sort, SortBy } from '../utils/sortUtils'
import { simpleTextCompareFn } from '../utils/text'

export type EmployeeInfo = EmployeeInfoResponse
export type Team = TeamTreeResponse
export type TeamInfo = TeamInfoResponse
export type TeamStatus = TeamStatusDTO
export type EmployeeOverview = EmployeeOverviewProjection
export type TeamUpdate = UpdateTeamRequest

export interface TeamDetails extends TeamDeepResponse {
  employees: EmployeeInfo[]
  leaders: EmployeeInfo[]
}

const normalizeCollection = <T>(data: unknown): T[] => {
  if (!data) return []
  if (Array.isArray(data)) return data as T[]
  if (data instanceof Set) return Array.from(data.values()) as T[]
  if (data instanceof Map) return Array.from(data.values()) as T[]
  if (typeof data === 'object') return Object.values(data as Record<string, T>)
  return []
}

const flattenTeams = (
  teams: TeamTreeResponse[] | Record<string, TeamTreeResponse> | null | undefined
): TeamTreeResponse[] => {
  let flatTeams: TeamTreeResponse[] = []
  const normalizedTeams = normalizeCollection<TeamTreeResponse>(teams)

  normalizedTeams.forEach(team => {
    flatTeams.push(team)

    if (team.children) {
      flatTeams = [
        ...flatTeams,
        ...flattenTeams(team.children as TeamTreeResponse[] | Record<string, TeamTreeResponse>),
      ]
    }
  })

  return flatTeams
}

const uniqueTeamsById = (teams: TeamTreeResponse[]): TeamTreeResponse[] => {
  const seenTeamIds = new Set<number>()

  return teams.filter(team => {
    if (seenTeamIds.has(team.id)) return false
    seenTeamIds.add(team.id)
    return true
  })
}

const TeamStatusCustomSortFn: Partial<Record<string, (item1: TeamStatus, item2: TeamStatus) => number>> = {
  score: (item1, item2) => ((item2.score || 0) > (item1.score || 0) ? 1 : -1),
  finished: (item1, item2) => ((item2.finished || 0) > (item1.finished || 0) ? 1 : -1),
  leaders: (item1, item2) => {
    const item1Leaders = item1.leaders || []
    const item2Leaders = item2.leaders || []
    return simpleTextCompareFn(
      Array.from(item1Leaders.values()).join(', '),
      Array.from(item2Leaders.values()).join(', ')
    )
  },
}

export class TeamService {
  static async findAllDetailed(): Promise<TeamDetails[]> {
    return teamAPI.findDeepUsingGET().then(response => {
      return normalizeCollection<TeamDetails>(response.data)
    })
  }

  static async findAll(): Promise<Team[]> {
    return teamAPI.findUsingGET5().then(response => {
      return normalizeCollection<Team>(response.data)
    })
  }

  static async findAllFlat(): Promise<Team[]> {
    return this.findAll().then(teams => {
      return uniqueTeamsById(flattenTeams(teams))
    })
  }

  static async findOne(teamId: number): Promise<TeamResponse> {
    return teamAPI.getByIdUsingGET2(teamId).then(response => {
      return response.data as TeamResponse
    })
  }

  static async findTeamStatus(): Promise<Set<TeamStatus>> {
    return teamAPI.findTeamStatusesUsingGET().then(response => {
      return response.data as Set<TeamStatus>
    })
  }

  static async findTeamInfo(teamId: number): Promise<TeamInfoResponse> {
    return teamAPI.getTeamInfoByIdUsingGET(teamId).then(response => {
      return response.data as TeamInfoResponse
    })
  }

  static sortTeamStatus(records: TeamStatus[], sorting: SortBy[]): TeamStatus[] {
    return sort<TeamStatus>(records, sorting, TeamStatusCustomSortFn)
  }

  static async findTeamMembers(teamId: number): Promise<EmployeeOverview[]> {
    return teamAPI.getTeamEmployeesUsingGET(teamId).then(response => {
      return normalizeCollection<EmployeeOverview>(response.data)
    })
  }

  static async updateTeamName(teamId: number, newTeamName: string) {
    await teamAPI.updateTeamNameUsingPUT(teamId, newTeamName)
  }
}
